import {
  DocumentOrganizationKeys,
  FilterDocument,
  FilterDocumentDateCustomTypes,
  FilterDocumentDateFilterTypes,
  FilterOptionKeys,
  GroupByMode,
  SortingMode,
  TaskGroupBy,
  TaskType,
  TodayGroupBy,
  UserSettings,
} from '@griply/types';
import { endOfDay } from 'date-fns';

import { pickFilterOptions } from './pick-filter-options';

const todaySavedFilterKeys = [
  FilterOptionKeys.LifeArea,
  FilterOptionKeys.Goal,
  FilterOptionKeys.GoalActive,
  FilterOptionKeys.GoalImpact,
  FilterOptionKeys.Duration,
  FilterOptionKeys.Priority,
] as const;

export const buildTodayDefaultFilter = (today: Date): FilterDocument => {
  const scheduledBranch = {
    [FilterOptionKeys.Date]: {
      type: FilterDocumentDateFilterTypes.Custom,
      custom: {
        type: FilterDocumentDateCustomTypes.Before,
        start: null,
        end: endOfDay(today),
      },
    },
  };

  const deadlineBranch = {
    [FilterOptionKeys.Deadline]: {
      type: FilterDocumentDateFilterTypes.Custom,
      custom: {
        type: FilterDocumentDateCustomTypes.Before,
        start: null,
        end: endOfDay(today),
      },
    },
  };

  return {
    [DocumentOrganizationKeys.GroupByMode]: GroupByMode.Deadline as TaskGroupBy,
    [DocumentOrganizationKeys.SortingMode]: SortingMode.DueDate,
    [DocumentOrganizationKeys.Filters]: {
      [FilterOptionKeys.Type]: null,
      [FilterOptionKeys.Tag]: null,
    },
    [DocumentOrganizationKeys.Or]: [scheduledBranch, deadlineBranch],
  };
};

/*
  What Today holds, ignoring the filters the user put on the screen. The daily task summary reports the day,
  so a life-area or tag filter on Today must not shrink the number it sends — only `todayHideHabits` applies,
  because a user who hides habits from Today does not consider them part of their day at all.
*/
export const buildTodaySummaryFilterDocument = (
  today: Date,
  settings?: UserSettings,
): FilterDocument => {
  const baseDocument = buildTodayDefaultFilter(today);

  if (!settings?.todayHideHabits) {
    return baseDocument;
  }

  return {
    ...baseDocument,
    [DocumentOrganizationKeys.Filters]: {
      ...baseDocument[DocumentOrganizationKeys.Filters],
      [FilterOptionKeys.Type]: {
        include: Object.values(TaskType).filter(
          (type) => type !== TaskType.Habit,
        ),
      },
    },
  };
};

export const buildTodayFilterDocument = (
  today: Date,
  settings?: UserSettings,
): FilterDocument => {
  const baseDocument = buildTodayDefaultFilter(today);

  if (!settings) {
    return baseDocument;
  }

  const groupByMode =
    (settings.todayGroupBy
      ? settings.todayGroupBy === TodayGroupBy.None
        ? GroupByMode.Deadline
        : (settings.todayGroupBy as unknown as TaskGroupBy)
      : undefined) ?? baseDocument[DocumentOrganizationKeys.GroupByMode];

  const sortingMode =
    settings.todoSortingMode ??
    baseDocument[DocumentOrganizationKeys.SortingMode];

  const typeFilter = settings.todayHideHabits
    ? {
        include: Object.values(TaskType).filter(
          (type) => type !== TaskType.Habit,
        ),
      }
    : baseDocument[DocumentOrganizationKeys.Filters]?.[FilterOptionKeys.Type];

  const savedFilters =
    settings.filters?.today?.[DocumentOrganizationKeys.Filters];

  const savedTagFilter = savedFilters?.[FilterOptionKeys.Tag];

  const tagFilter =
    settings.todayFilterByTags?.length || savedTagFilter?.noTag
      ? {
          include: settings.todayFilterByTags?.length
            ? settings.todayFilterByTags
            : null,
          noTag: savedTagFilter?.noTag ?? null,
          matchMode: savedTagFilter?.matchMode ?? null,
        }
      : baseDocument[DocumentOrganizationKeys.Filters]?.[FilterOptionKeys.Tag];

  return {
    ...baseDocument,
    [DocumentOrganizationKeys.GroupByMode]: groupByMode,
    [DocumentOrganizationKeys.SortingMode]: sortingMode,
    [DocumentOrganizationKeys.Filters]: {
      ...baseDocument[DocumentOrganizationKeys.Filters],
      [FilterOptionKeys.Type]: typeFilter,
      [FilterOptionKeys.Tag]: tagFilter,
      ...pickFilterOptions(savedFilters, todaySavedFilterKeys),
    },
    [DocumentOrganizationKeys.Or]: baseDocument[DocumentOrganizationKeys.Or],
    [DocumentOrganizationKeys.CollapsedSections]:
      settings.filters?.today?.[DocumentOrganizationKeys.CollapsedSections],
  };
};
